/**
 * Strips embedded metadata from an image before upload.
 *
 * The motivating case: AI image tools (DALL·E, Firefly, and others) write C2PA
 * "content credentials" into the file, and that manifest embeds an `<svg>` icon.
 * A web-application firewall in front of the server scans the multipart upload
 * body, matches the `<svg>`/`<path>` markup with its cross-site-scripting rules,
 * and returns 403 before the request ever reaches the API — so the upload fails
 * for reasons that have nothing to do with the image itself.
 *
 * Re-encoding the pixels through a canvas produces a clean file with none of the
 * original ancillary chunks — no C2PA, no EXIF, no embedded SVG — so the WAF has
 * nothing to match. PNG is re-encoded losslessly, keeping screenshot text crisp;
 * this is the reason it is worth doing here rather than only downscaling photos.
 *
 * `downscale-image.ts` handles JPEG/WebP (which it already re-encodes, so their
 * metadata is dropped as a side effect). This module handles PNG, which that one
 * deliberately leaves uncompressed — every PNG is re-encoded here, losslessly,
 * so no chunk survives to reach the firewall no matter where a tool hid it.
 */

/**
 * Returns a metadata-free PNG, or the original file when the re-encode is not
 * possible.
 *
 * Every PNG is re-encoded through the canvas, unconditionally — not only ones
 * that a chunk scan flagged. PNG re-encoding is lossless, so a screenshot's text
 * stays pixel-for-pixel crisp, and doing it for every PNG means no metadata can
 * reach the firewall regardless of which chunk an AI tool chose to hide it in
 * (a conditional scan can only catch the chunk types it already knows about).
 * The one-time cost is a canvas pass per PNG upload, which is negligible next to
 * the network transfer.
 *
 * Never throws: a failed strip must not fail the upload — a large-but-clean file
 * is a better outcome than a blocked one, and the reverse (blocking on a strip
 * error) would trade one failure for another. Only PNGs are handled here; every
 * other type is returned untouched for downscale-image.ts to deal with.
 */
export async function stripPngMetadata(file: File): Promise<File> {
  if (file.type !== "image/png") return file;

  let bitmap: ImageBitmap;
  try {
    // Decode from the original File (a Blob) rather than re-wrapping the bytes,
    // which keeps the buffer typing simple.
    bitmap = await createImageBitmap(file);
  } catch {
    // Cannot decode it here; let the server validate the original bytes.
    return file;
  }

  try {
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext("2d");
    if (!context) return file;
    context.drawImage(bitmap, 0, 0);

    // PNG is lossless, so re-encoding keeps every pixel — including crisp text —
    // while dropping the metadata chunks that came with the original.
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((result) => resolve(result), "image/png");
    });
    if (!blob) return file;

    return new File([blob], file.name, { type: "image/png", lastModified: file.lastModified });
  } catch {
    return file;
  } finally {
    bitmap.close();
  }
}
